/* eslint-disable no-undef */
import { getNextColor, IChartProps } from "@fluentui/react-charting";
import {
  getGridDateOperators,
  getGridNumericOperators,
  getGridStringOperators,
  GridFilterModel,
  GridLogicOperator,
  GridRowSelectionModel,
  GridSortModel
} from "@mui/x-data-grid";
import { GridStateColDef } from "@mui/x-data-grid/internals";
import axios from "axios";
import { Dispatch, SetStateAction } from "react";
import { ILineChartData } from "../components/ProductView/Common/ProductView.interfaces";
import { featureRouteMap } from "../constants/FeatureConfig.constants";
import { IAzSearchInput } from "../interfaces/Customer.interfaces";
import { IAPIError } from "../interfaces/DisplayAPIsException.interfaces";
import { FeatureRestrictionConfig } from "../interfaces/FeatureRestrictionConfig.interfaces";
import { FilterCriteria, IRows } from "../SharedComponents/Interfaces/CustomDataGrid.interfaces";

export const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Formats a date string for display as dd-MON-yyyy (e.g. 05-Dec-2026).
 * Handles dd-MM-yyyy strings from the backend directly, and converts ISO/other formats.
 * Returns "—" for missing or invalid values.
 */
export const formatDisplayDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "—";
  try {
    const ddMMyyyyMatch = dateStr.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (ddMMyyyyMatch) {
      const [, day, month, year] = ddMMyyyyMatch;
      const mon = MONTHS_SHORT[parseInt(month, 10) - 1] ?? month;
      return `${day.padStart(2, "0")}-${mon}-${year}`;
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "—";
    const d = String(date.getDate()).padStart(2, "0");
    const mon = MONTHS_SHORT[date.getMonth()];
    return `${d}-${mon}-${date.getFullYear()}`;
  } catch {
    return "—";
  }
};

export const formatCountWithCommas = (value: any): string => {
  if (value === null || value === undefined) return "";
  return Number(value).toLocaleString();
};

export const objSortByValLength = (obj: { [key: string]: any[] }) =>
  Object.entries(obj)
    .sort(([, a], [, b]): any => b.length - a.length)
    .reduce((r, [k, v]) => ({ ...r, [k]: v }), {});

export const groupBy = (xs: any[], key: string) =>
  xs.reduce((rv, x) => {
    (rv[x[key]] ?? (rv[x[key]] = [])).push(x);
    return rv;
  }, {});

export const groupByAverage = (xs: any[], key1: string, key2: string) => {
  return xs.reduce((rv, x) => {
    if (!rv[x[key1]]) {
      rv[x[key1]] = { sum: 0, count: 0 };
    }
    rv[x[key1]].sum += parseInt(x[key2]);
    rv[x[key1]].count += 1;
    return rv;
  }, {});
};

export const splitColumnValues = (data: any[], column: string, splitString: string) => {
  const newData: any[] = [];
  const filteredData = data.filter((entry) => entry[column]);
  filteredData.forEach((entry) => {
    // Split the values in the specified column by comma
    const values = entry[column].split(splitString); // Create new entries for each value

    values.forEach((value: string) => {
      const newEntry = { ...entry, [column]: value.trim() };
      newData.push(newEntry);
    });
  });

  return newData;
};

export const calculateAverages = (groupedData: any[]) => {
  const averages: { [key: string]: number } = {};
  for (const key in groupedData) {
    averages[key] = groupedData[key].sum / groupedData[key].count;
  }

  return Object.keys(averages).map((item) => ({
    name: item,
    value: averages[item]
  }));
};

export const arrSortAscByKeyLength = ({ key, data }: { key: string; data: any[] }) =>
  data.toSorted((a, b) => a[key]?.length - b[key]?.length);

export const arrSortDescByKeyLength = ({ key, data }: { key: string; data: any[] }) =>
  data.toSorted((a, b) => b[key]?.length - a[key]?.length);

export const arrSortAscByKeyValue = ({ key, data }: { key: string; data: any[] }) => {
  return data.toSorted((a, b) => {
    if (a[key] > b[key]) return 1;
    return b[key] > a[key] ? -1 : 0;
  });
};

export const getLastDateUTC = (dateMonthNumber: string, dateYear: number) => {
  const dateMonthIndex = MONTHS_SHORT.indexOf(dateMonthNumber) + 1;
  const dateMonth = dateMonthIndex < 10 ? `0${dateMonthIndex}` : dateMonthIndex;
  const dateLastDay = new Date(Number(dateYear), Number(dateMonth), 0).getDate();
  return new Date(`${Number(dateYear)}-${dateMonth}-${dateLastDay}T00:00:00.000Z`);
};

export const configAxiosToken = (token: string) =>
  axios.create({
    headers: {
      Authorization: `Bearer ${token}`,
      Environment: process.env.REACT_APP_ENVIRONMENT
    }
  });

export const copy = async (message: string) => {
  let status = false;
  const textArea = document.createElement("textarea");
  textArea.value = message;
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const toCopy = message;
    await navigator.clipboard.writeText(toCopy);
    status = true;
  } catch (err) {
    console.error("Failed to copy: ", err);
    status = false;
  } finally {
    document.body.removeChild(textArea);
  }
  return status ? Promise.resolve(true) : Promise.reject(new Error("Failed to copy"));
};

/**
 * Copies the provided HTML string to the clipboard, preserving formatting.
 * Uses execCommand('copy') first, then tries Clipboard API as a fallback.
 * @param htmlString The HTML string to copy.
 * @returns Promise<boolean> indicating success or failure.
 */
export const copyFormattedToClipboard = async (htmlString: string): Promise<boolean> => {
  if (!htmlString) return false;

  // First, try execCommand('copy')
  const temp = document.createElement("div");
  temp.innerHTML = htmlString;
  temp.style.position = "fixed";
  temp.style.pointerEvents = "none";
  temp.style.opacity = "0";
  document.body.appendChild(temp);

  const range = document.createRange();
  range.selectNodeContents(temp);

  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);

  let success = false;
  try {
    success = document.execCommand("copy");
    if (success) {
      selection?.removeAllRanges();
      document.body.removeChild(temp);
      return true;
    }
  } catch (err) {
    // Continue to Clipboard API fallback
    console.warn("execCommand failed, trying Clipboard API.", err);
  }

  selection?.removeAllRanges();
  document.body.removeChild(temp);

  // Fallback: Clipboard API for HTML content
  if (navigator.clipboard && (window.ClipboardItem || (window as any).ClipboardItem)) {
    try {
      const clipboardItem = new window.ClipboardItem({
        "text/html": new Blob([htmlString], { type: "text/html" }),
        "text/plain": new Blob([temp.innerText], { type: "text/plain" })
      });
      await navigator.clipboard.write([clipboardItem]);
      return true;
    } catch (err) {
      console.error("Clipboard API failed.", err);
    }
  }

  return false;
};

const formatDateForFilter = (value: any): string | undefined => {
  if (!value) return undefined;

  // If it's a Date object, convert to ISO string
  if (value instanceof Date) {
    const isoString = value.toISOString();
    // Format as: 2026-02-02T05:30:00+00:00 (replacing Z with +00:00)
    return isoString.replace("Z", "+00:00");
  }

  // If it's already a string, validate and convert to ISO format
  if (typeof value === "string") {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      const isoString = date.toISOString();
      return isoString.replace("Z", "+00:00");
    }
    // Return an empty string if parsing fails
    return "";
  }

  return undefined;
};

export const applyAzSearchInputFilter = (
  operator: string,
  field: string,
  value: string | undefined
): string | undefined => {
  let filter = undefined;

  switch (operator) {
    case "contains":
      if ("+-&|!(){}[]^\"~*?:/ ".split("").filter((f) => (value ?? "").includes(f)).length) {
        filter = `search.ismatch('"${value}"', '${field}', 'full', 'any')`;
        break;
      }
      filter = `search.ismatch('/.*${value}.*/', '${field}', 'full', 'any')`;
      break;
    case "equals":
      filter = `${field} eq '${value}'`;
      break;
    case "startsWith":
      filter = `search.ismatch('${value}*', '${field}', 'full', 'any')`;
      break;
    case "endsWith":
      filter = `search.ismatch('/.*${value}/', '${field}', 'full', 'any')`;
      break;
    case "is":
      {
        const formattedValueIs = formatDateForFilter(value);
        filter = formattedValueIs ? `${field} eq ${formattedValueIs}` : undefined;
        break;
      }
    case "not":
      {
        const formattedValueNot = formatDateForFilter(value);
        filter = formattedValueNot ? `${field} ne ${formattedValueNot}` : undefined;
        break;
      }
    case "after":
      {
        const formattedValueAfter = formatDateForFilter(value);
        filter = formattedValueAfter ? `${field} gt ${formattedValueAfter}` : undefined;
        break;
      }
    case "onOrAfter":
      {
        const formattedValueOnOrAfter = formatDateForFilter(value);
        filter = formattedValueOnOrAfter ? `${field} ge ${formattedValueOnOrAfter}` : undefined;
        break;
      }
    case "before":
      {
        const formattedValueBefore = formatDateForFilter(value);
        filter = formattedValueBefore ? `${field} lt ${formattedValueBefore}` : undefined;
        break;
      }
    case "onOrBefore":
      {
        const formattedValueOnOrBefore = formatDateForFilter(value);
        filter = formattedValueOnOrBefore ? `${field} le ${formattedValueOnOrBefore}` : undefined;
        break;
      }
    case "=":
      filter = `${field} eq ${value}`;
      break;
    case "!=":
      filter = `${field} ne ${value}`;
      break;
    case ">":
      filter = `${field} gt ${value}`;
      break;
    case ">=":
      filter = `${field} ge ${value}`;
      break;
    case "<":
      filter = `${field} lt ${value}`;
      break;
    case "<=":
      filter = `${field} le ${value}`;
      break;
    case "isEmpty":
      filter = `search.ismatch('/^$|\\s+/', '${field}', 'full', 'any')`;
      break;
    case "isNotEmpty":
      filter = `${field} ne null or ${field} ne '' or ${field} ne ' '`;
      break;
    default:
      break;
  }

  return filter;
};

export const isNumberOnly = (value: any) => new RegExp(/^\d+$/).test(value);

export const isTenantIdOnly = (value: any) =>
  new RegExp(/^[A-Za-z0-9]{8,12}(-[A-Za-z0-9]{4}){3}-[A-Za-z0-9]{12}/).test(value);

export const handleToggleDataGridRowSelection = (
  selectionModel: GridRowSelectionModel,
  setSelectionModel: React.Dispatch<React.SetStateAction<GridRowSelectionModel>>
) => {
  return (rowSelectionModel: GridRowSelectionModel) => {
    if (rowSelectionModel.at(0) !== selectionModel.at(0)) {
      setSelectionModel(rowSelectionModel);
    } else {
      setSelectionModel([]);
    }
  };
};

export function copyAndSort<T>(items: T[], columnKey: string, isSortedDescending?: boolean): T[] {
  const key = columnKey as keyof T;
  return items.slice(0).sort((a: T, b: T) => ((isSortedDescending ? a[key] < b[key] : a[key] > b[key]) ? 1 : -1));
}

export const Timer = (callback: () => void, delay: number = 1000) => {
  let timerId: number | undefined,
    start: number,
    remaining = delay;

  const pause = () => {
    window.clearTimeout(timerId);
    timerId = undefined;
    remaining -= Date.now() - start;
  };

  const resume = () => {
    if (timerId) {
      return;
    }

    start = Date.now();
    timerId = window.setTimeout(callback, remaining);
  };

  const clearTimeout = () => window.clearTimeout(timerId);

  resume();

  return {
    pause,
    resume,
    clearTimeout
  };
};

export const getLineChartData = (data: ILineChartData[]): IChartProps => ({
  chartTitle: "Line Chart",
  lineChartData: data.map(({ name, data }, index) => ({
    legend: name,
    data: data.map(({ x, y }) => ({
      x: new Date(new Date(x).toISOString()),
      y: y
    })),
    color: getNextColor(index),
    lineOptions: {
      lineBorderWidth: "4"
    }
  }))
});

export const getSumForNumberValues = (values: number[]) => values.reduce((total, item) => total + (item || 0), 0);

export function callSearchAPIs(
  promise: Promise<any>,
  setLoading: Dispatch<SetStateAction<boolean>>,
  setRows: Dispatch<SetStateAction<IRows>>,
  setError?: Dispatch<SetStateAction<boolean>>
) {
  setLoading(true);

  promise
    .then((rows) => {
      setRows(rows);
      setLoading(false);
    })
    .catch(() => setError?.(true));
}
export function getSearchInput(
  initialFilters: string,
  searchText: string,
  filterOptions: GridFilterModel,
  sortOptions: GridSortModel,
  pagination: {
    pageSize: number;
    page: number;
  },
  callAPIsType: string
) {
  const { items } = filterOptions;

  const searchInput: IAzSearchInput = {
    searchText: `${searchText.replaceAll(" ", "+")}*`,
    size: pagination.pageSize,
    skip: pagination.pageSize * pagination.page,
    orderBy: sortOptions.map((m) => `${m.field.charAt(0).toUpperCase().concat(m.field.slice(1))} ${m.sort}`)
  };

  if (callAPIsType === "customer") {
    if (isNumberOnly(searchText)) {
      searchInput.searchText = "*";
      searchInput.filter = applyAzSearchInputFilter("=", "TopParentId", searchText);
    }
    if (isTenantIdOnly(searchText)) {
      searchInput.searchText = "*";
      searchInput.filter = applyAzSearchInputFilter("=", "TenantId", `'${searchText.replaceAll("'", "\"")}'`);
    }
  }

  if (items.length > 0) {
    const filterParts: string[] = [];

    items.forEach((item) => {
      const { field, operator, value } = item;
      const _field = field.charAt(0).toUpperCase().concat(field.slice(1));

      if (operator === "isAnyOf" && Array.isArray(value)) {
        const arrayFilterParts = value.map((val) => applyAzSearchInputFilter("equals", _field, val));
        const arrayFilterStr = arrayFilterParts.filter(Boolean).join(" or ");
        if (arrayFilterStr) {
          filterParts.push(`(${arrayFilterStr})`);
        }
      } else {
        const requiresValue = !["isEmpty", "isNotEmpty"].includes(operator);
        if (!requiresValue || (value !== undefined && value !== null && value !== "")) {
          const filterStr = applyAzSearchInputFilter(operator, _field, value);
          if (filterStr) {
            filterParts.push(filterStr);
          }
        }
      }
    });

    if (filterParts.length > 0) {
      const logicOp = filterOptions.logicOperator === GridLogicOperator.Or ? "or" : "and";
      const combinedFilter = filterParts.length === 1 ? filterParts[0] : `(${filterParts.join(` ${logicOp} `)})`;

      searchInput.filter = searchInput.filter ? `${searchInput.filter} and ${combinedFilter}` : combinedFilter;
    }
  }

  if (initialFilters) {
    if (searchInput.filter) {
      searchInput.filter += ` and ( ${initialFilters} )`;
    } else {
      searchInput.filter = initialFilters;
    }
  }

  return searchInput;
}

export function getPartnerSearchInput(
  initialFilters: string,
  searchText: string,
  filterOptions: GridFilterModel,
  sortOptions: GridSortModel,
  pagination: {
    pageSize: number;
    page: number;
  }
) {
  const { items } = filterOptions;

  const searchInput: IAzSearchInput = {
    searchText: `${searchText.replaceAll(" ", "+")}*`,
    size: pagination.pageSize,
    skip: pagination.pageSize * pagination.page,
    orderBy: sortOptions.map((m) => {
      let field = m.field;
      switch (field) {
        case "mpnId":
          field = "MPNId";
          break;
        case "mpnName":
          field = "MPNName";
          break;
        case "cspCustomersCount":
          field = "CSPCustomersCount";
          break;
        case "cporCustomersCount":
          field = "CPORCustomersCount";
          break;
        case "palCustomersCount":
          field = "PALCustomersCount";
          break;
        case "cspLoginMAU":
          field = "CSPLoginMAU";
          break;
        case "cporLoginMAU":
          field = "CPORLoginMAU";
          break;
        case "palLoginMAU":
          field = "PALLoginMAU";
          break;
        default:
          field = field.charAt(0).toUpperCase().concat(field.slice(1));
      }
      return `${field} ${m.sort}`;
    })
  };

  if (items.length > 0) {
    const filterParts: string[] = [];

    items.forEach((item) => {
      const { field, operator, value } = item;
      let _field = field;
      switch (field) {
        case "mpnId":
          _field = "MPNId";
          break;
        case "mpnName":
          _field = "MPNName";
          break;
        case "cspCustomersCount":
          _field = "CSPCustomersCount";
          break;
        case "cporCustomersCount":
          _field = "CPORCustomersCount";
          break;
        case "palCustomersCount":
          _field = "PALCustomersCount";
          break;
        case "cspLoginMAU":
          _field = "CSPLoginMAU";
          break;
        case "cporLoginMAU":
          _field = "CPORLoginMAU";
          break;
        case "palLoginMAU":
          _field = "PALLoginMAU";
          break;
        default:
          _field = field.charAt(0).toUpperCase().concat(field.slice(1));
      }

      if (operator === "isAnyOf" && Array.isArray(value)) {
        const arrayFilterParts = value.map((val) => applyAzSearchInputFilter("equals", _field, val));
        const arrayFilterStr = arrayFilterParts.filter(Boolean).join(" or ");
        if (arrayFilterStr) {
          filterParts.push(`(${arrayFilterStr})`);
        }
      } else {
        const requiresValue = !["isEmpty", "isNotEmpty"].includes(operator);
        if (!requiresValue || (value !== undefined && value !== null && value !== "")) {
          const filterStr = applyAzSearchInputFilter(operator, _field, value);
          if (filterStr) {
            filterParts.push(filterStr);
          }
        }
      }
    });

    if (filterParts.length > 0) {
      const logicOp = filterOptions.logicOperator === GridLogicOperator.Or ? "or" : "and";
      const combinedFilter = filterParts.length === 1 ? filterParts[0] : `(${filterParts.join(` ${logicOp} `)})`;
      searchInput.filter = combinedFilter;
    }
  }

  if (initialFilters) {
    if (searchInput.filter) {
      searchInput.filter += ` and ( ${initialFilters} )`;
    } else {
      searchInput.filter = initialFilters;
    }
  }
  return searchInput;
}

// / Milliseconds in one day = 86400000
export const getDaysBetweenDates = (dateOne: Date, dateTwo: Date) =>
  Math.ceil(Math.abs(dateTwo.getTime() - dateOne.getTime()) / 86400000);

// Debounce function
export const debounce = (callback: (...args: any[]) => void, delay: number = 1000) => {
  let debounceTimeout: ReturnType<typeof setTimeout> | null;

  return function (...args: any[]) {
    if (debounceTimeout) clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(() => {
      debounceTimeout = null;
      callback(...args); // Pass all arguments to the callback
    }, delay);
  };
};

// Return abbrevated value like 5655 => 5.65k
export const getAbbrevatedValue = (
  value: number,
  requiredNotation?: "compact" | "standard" | "scientific" | "engineering",
  maxiFractionDigits?: number
) =>
  Intl.NumberFormat("en-US", {
    notation: requiredNotation ? requiredNotation : "compact",
    maximumFractionDigits: maxiFractionDigits ? maxiFractionDigits : 2
  }).format(value);

// Checks if string is valid JSON
export const isValidJson = (str: string) => {
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
};

/**
 * Maps the default MUI DataGrid date filter operators to user-friendly labels.
 * For each operator, if a label is already provided, it is used; otherwise, a custom label is assigned
 * based on the operator's value. This ensures all date filter operators display clear, readable text
 * in the filter UI.
 */
export const dateOperators = getGridDateOperators().map((op) => ({
  ...op,
  label:
    op.label ??
    {
      is: "is",
      not: "is not",
      after: "is after",
      onOrAfter: "is on or after",
      before: "is before",
      onOrBefore: "is on or before",
      isEmpty: "is empty",
      isNotEmpty: "is not empty"
    }[op.value] ??
    op.value
}));

/**
 * Maps the default MUI DataGrid string filter operators to user-friendly labels.
 * For each operator, if a label is already provided, it is used; otherwise, a custom label is assigned
 * based on the operator's value. This ensures all string filter operators display clear, readable text
 * in the filter UI.
 */
export const stringOperators = getGridStringOperators().map((op) => ({
  ...op,
  label:
    op.label ??
    {
      contains: "contains",
      doesNotContain: "does not contain",
      equals: "equals",
      doesNotEqual: "does not equal",
      startsWith: "starts with",
      endsWith: "ends with",
      isEmpty: "is empty",
      isNotEmpty: "is not empty",
      isAnyOf: "is any of"
    }[op.value] ??
    op.value
}));

/**
 * Maps the default MUI DataGrid number filter operators to user-friendly labels.
 * For each operator, if a label is already provided, it is used; otherwise, a custom label is assigned
 * based on the operator's value. This ensures all number filter operators display clear, readable text
 * in the filter UI.
 */
export const numberOperators = getGridNumericOperators().map((op) => ({
  ...op,
  label:
    op.label ??
    {
      isEmpty: "is empty",
      isNotEmpty: "is not empty"
    }[op.value] ??
    op.value
}));

// Helper to get operator label
export const getOperatorLabel = (
  column: GridStateColDef | undefined,
  operatorValue: string,
  operatorItem: { label?: string; value: string }
) => {
  if (column?.type === "date") {
    return dateOperators.find((item) => item.value === operatorValue)?.label ?? operatorItem.label ?? operatorValue;
  }
  if (column?.type === "string") {
    return stringOperators.find((item) => item.value === operatorValue)?.label ?? operatorItem.label ?? operatorValue;
  }
  if (column?.type === "number") {
    return numberOperators.find((item) => item.value === operatorValue)?.label ?? operatorItem.label ?? operatorValue;
  }
  return operatorItem.label ?? operatorValue;
};

/**
 * customDataGridApplyFilters
 * --------------------------
 * Filters an array of items based on a set of filter criteria and a logic operator (AND/OR).
 * - Supports filtering for dates, strings, numbers, and booleans.
 * - Handles various operators for each type (e.g., "contains", "equals", "isEmpty", "after", etc.).
 * - For date comparisons, converts values to Date objects and compares accordingly.
 * - For string, number, and boolean types, applies relevant operators.
 * - Returns a new array containing only items that match the filter criteria.
 *
 * @template Type - The type of items in the array.
 * @param items - The array of items to filter.
 * @param criteria - An array of filter criteria objects.
 * @param logicOperator - Determines if all (AND) or any (OR) criteria must match.
 * @returns Filtered array of items.
 */
export const customDataGridApplyFilters = <Type,>(
  items: Array<Type>,
  criteria: FilterCriteria[],
  logicOperator: GridLogicOperator = GridLogicOperator.And
): Type[] => {
  return items.filter((item: any) => {
    const predicate = (criterion: FilterCriteria) => {
      const itemValue = item[criterion.key];
      // Handle date comparison if either value is a Date
      if ((itemValue instanceof Date || criterion.value instanceof Date) && (itemValue || criterion.value)) {
        // Convert both to Date objects if possible
        let itemDate: Date | undefined;
        if (itemValue instanceof Date) {
          itemDate = itemValue;
        } else if (itemValue) {
          itemDate = new Date(itemValue.slice(0, 10));
        } else {
          itemDate = undefined;
        }
        let criterionDate: Date | undefined;
        if (criterion.value instanceof Date) {
          criterionDate = criterion.value;
        } else if (typeof criterion.value === "string" || typeof criterion.value === "number") {
          criterionDate = new Date(criterion.value);
        } else {
          criterionDate = undefined;
        }
        if (!itemDate || !criterionDate || isNaN(itemDate.getTime()) || isNaN(criterionDate.getTime())) {
          return false;
        }
        switch (criterion.operator) {
          case "is":
            return itemDate.getTime() === criterionDate.getTime();
          case "not":
            return itemDate.getTime() !== criterionDate.getTime();
          case "after":
            return itemDate.getTime() > criterionDate.getTime();
          case "onOrAfter":
            return itemDate.getTime() >= criterionDate.getTime();
          case "before":
            return itemDate.getTime() < criterionDate.getTime();
          case "onOrBefore":
            return itemDate.getTime() <= criterionDate.getTime();
          case "is empty":
            return itemValue === undefined;
          case "is not empty":
            return itemValue !== undefined;
          default:
            return false;
        }
      } else if (typeof itemValue === "string" && typeof criterion.value === "string") {
        switch (criterion.operator) {
          case "contains":
            return itemValue.toLowerCase().includes(criterion.value.toLowerCase());
          case "doesNotContain":
            return !itemValue.toLowerCase().includes(criterion.value.toLowerCase());
          case "equals":
            return itemValue.toLowerCase() === criterion.value.toLowerCase();
          case "doesNotEqual":
            return itemValue.toLowerCase() !== criterion.value.toLowerCase();
          case "startsWith":
            return itemValue.toLowerCase().startsWith(criterion.value.toLowerCase());
          case "endsWith":
            return itemValue.toLowerCase().endsWith(criterion.value.toLowerCase());
          case "isEmpty":
            return itemValue === "";
          case "isNotEmpty":
            return itemValue !== "";
          default:
            return false;
        }
      } else if (typeof itemValue === "number" && typeof criterion.value === "number") {
        switch (criterion.operator) {
          case "=":
            return itemValue === criterion.value;
          case "!=":
            return itemValue !== criterion.value;
          case ">":
            return itemValue > criterion.value;
          case ">=":
            return itemValue >= criterion.value;
          case "<":
            return itemValue < criterion.value;
          case "<=":
            return itemValue <= criterion.value;
          case "isEmpty":
            return itemValue === undefined || itemValue === null;
          case "isNotEmpty":
            return itemValue !== undefined && itemValue !== null;
          default:
            return false;
        }
      } else if (typeof itemValue === "number" && typeof criterion.value === "string") {
        // Support numeric operators via coercion; fall back to string comparisons
        const parsed = Number(criterion.value);
        const isNumericOp = numberOperators.map(({ value }) => value).includes(criterion.operator);

        if (isNumericOp && Number.isFinite(parsed)) {
          switch (criterion.operator) {
            case "=":
              return itemValue === parsed;
            case "!=":
              return itemValue !== parsed;
            case ">":
              return itemValue > parsed;
            case ">=":
              return itemValue >= parsed;
            case "<":
              return itemValue < parsed;
            case "<=":
              return itemValue <= parsed;
            default:
              return false;
          }
        }

        const itemValueAsString = String(itemValue);
        const itemValueLowerCase = itemValueAsString.toLowerCase();
        const criterionValueLowerCase = criterion.value.toLowerCase();

        switch (criterion.operator) {
          case "contains":
            return itemValueLowerCase.includes(criterionValueLowerCase);
          case "doesNotContain":
            return !itemValueLowerCase.includes(criterionValueLowerCase);
          case "equals":
            return itemValueLowerCase === criterionValueLowerCase;
          case "doesNotEqual":
            return itemValueLowerCase !== criterionValueLowerCase;
          case "startsWith":
            return itemValueLowerCase.startsWith(criterionValueLowerCase);
          case "endsWith":
            return itemValueLowerCase.endsWith(criterionValueLowerCase);
          case "isEmpty":
            return itemValue === undefined || itemValue === null;
          case "isNotEmpty":
            return itemValue !== undefined && itemValue !== null;
          default:
            return false;
        }
      } else if (typeof itemValue === "boolean" && (typeof criterion.value === "boolean" || criterion.value === "")) {
        switch (criterion.value) {
          case true:
            return itemValue === true;
          case false:
            return itemValue === false;
          default:
            return true; // If no specific value is provided, consider it a match
        }
      } else if (criterion.operator === "isEmpty") {
        return itemValue === undefined || itemValue === null;
      } else if (criterion.operator === "isNotEmpty") {
        return itemValue !== undefined && itemValue !== null;
      }
      return false;
    };

    if (logicOperator === GridLogicOperator.Or) {
      return criteria.some(predicate);
    }
    // Default to "and"
    return criteria.every(predicate);
  });
};

/**
 * Adds a unique id property to each item in the array.
 * @param items Array of objects to add id to.
 * @param idKey Name of the id property to add. Default is 'id'.
 * @param prefix Optional prefix for the id value.
 * @returns New array with id property added.
 */
export function addUniqueId<T extends object>(
  items: T[] = [],
  idKey: string = "id",
  prefix: string = ""
): (T & { [key: string]: string })[] {
  return items.map((item, index) => ({
    ...item,
    [idKey]: `${prefix}${index + 1}`
  }));
}

export const convertIsoToDate = (value: string) => {
  const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
  if (value && dateRegex.test(value) && !isNaN(new Date(value).getTime())) {
    return new Date(value).toISOString().substring(0, 10);
  }
  return value;
};

export const aggregateDataByKey = (groupKey: string, data: any[], sumField: string) => {
  const arr = Array.isArray(data) ? data : [];
  const filteredData = arr.filter((item) => item?.[groupKey]);
  const groupedData = groupBy(filteredData, groupKey);

  return Object.keys(groupedData)?.map((item) => ({
    name: item,
    value: groupedData[item].reduce((acc: any, curr: any) => acc + (Number(curr[sumField]) || 0), 0)
  }));
};

export interface ShareableDataModel {
  title: string;
  text: string;
  url?: string;
}

/**
 * Reusable share function that supports native Web Share API with fallback to clipboard
 * @param linkSharingInfo - Object containing title, text, and optional URL
 * @param customUrl - Optional custom URL to override shareData.url or window.location.href
 * @returns Promise<boolean> - Returns true if successful, false otherwise
 */
export const createShareableLink = async (
  linkSharingInfo: ShareableDataModel,
  customUrl?: string
): Promise<boolean> => {
  const finalShareData = {
    ...linkSharingInfo,
    url: customUrl || linkSharingInfo.url || window.location.href
  };

  // Try native share API first (if available)
  if (navigator.share) {
    try {
      await navigator.share(finalShareData);
      return true;
    } catch (error) {
      console.error("Native share failed:", error);
      // Fall through to clipboard fallback
    }
  }

  // Clipboard fallback (used when native share is unavailable or fails)
  try {
    await navigator.clipboard.writeText(finalShareData.url);
    console.log("URL copied to clipboard");
    return true;
  } catch (clipboardError) {
    console.error("Failed to copy to clipboard:", clipboardError);
    return false;
  }
};

/**
 * Helper function to create share data for customer pages
 * @param customerName - Name of the customer
 * @param customUrl - Optional custom URL
 * @returns ShareData object
 */
export const createCustomerShareData = (customerName: string, customUrl?: string): ShareableDataModel => ({
  title: `${customerName} - Customer View`,
  text: `Check out ${customerName} customer details`,
  url: customUrl
});

/**
 * Helper function to create share data for product pages
 * @param productName - Name of the product
 * @param customUrl - Optional custom URL
 * @returns ShareData object
 */
export const createProductShareData = (productName: string, customUrl?: string): ShareableDataModel => ({
  title: `${productName} - Product View`,
  text: `Check out ${productName} product details`,
  url: customUrl
});

/**
 * Helper function to create share data for partner pages
 * @param partnerName - Name of the partner
 * @param customUrl - Optional custom URL
 * @returns ShareData object
 */
export const createPartnersShareData = (partnerName: string, customUrl?: string): ShareableDataModel => ({
  title: `${partnerName} - Partner View`,
  text: `Check out ${partnerName} partner details`,
  url: customUrl
});

/**
 * Safe base64 encoding that handles Unicode characters
 * Uses encodeURIComponent to properly handle non-ASCII characters before base64 encoding
 * @param str - String to encode
 * @returns Base64 encoded string that safely handles Unicode
 */
export const safeBase64Encode = (str: string): string => {
  try {
    // Convert string to UTF-8 bytes, then to base64
    return btoa(
      encodeURIComponent(str).replaceAll(/%([0-9A-F]{2})/g, (match, p1) => {
        return String.fromCodePoint(Number.parseInt(p1, 16));
      })
    );
  } catch (error) {
    console.warn("Failed to encode string to base64:", error);
    // Fallback: use URL encoding
    return encodeURIComponent(str);
  }
};

/**
 * Safe base64 decoding that handles Unicode characters
 * Properly decodes base64 strings that contain Unicode characters
 * @param str - Base64 encoded string to decode
 * @returns Decoded string with proper Unicode support
 */
export const safeBase64Decode = (str: string): string => {
  try {
    // Try base64 decoding first
    const decoded = atob(str);
    return decodeURIComponent(
      Array.prototype.map
        .call(decoded, (c: string) => {
          return "%" + ("00" + c.codePointAt(0)?.toString(16)).slice(-2);
        })
        .join("")
    );
  } catch (error) {
    // Fallback: assume it's URL encoded
    try {
      return decodeURIComponent(str);
    } catch (fallbackError) {
      console.warn("Failed to decode string:", error, fallbackError);
      return str; // Return original string as last resort
    }
  }
};

const hasValue = (val: unknown): boolean =>
  typeof val === "string" ? val.trim() !== "" : val !== undefined && val !== null;

/**
 * Simple approach: place each disabled item only in its deepest populated level
 *
 * @example
 * { feature: "F1", isDisabled: true } → disabled.feature
 * { feature: "F1", tab: "T1", isDisabled: true } → disabled.tab
 * { feature: "F1", tab: "T1", subTab: "S1", isDisabled: true } → disabled.subTab
 */
export function buildDisabledFeatureLookup(
  entries: FeatureRestrictionConfig[]
): Record<string, FeatureRestrictionConfig[]> {
  if (!Array.isArray(entries)) return {};

  const keys = ["feature", "tab", "subTab", "section", "viewBy"];
  const disabled: Record<string, FeatureRestrictionConfig[]> = {};
  const keyArray = Array.isArray(keys) ? keys : [keys];

  entries.forEach((item) => {
    if (!item?.isDisabled) return;

    // Pick the deepest populated key (allowing gaps)
    let deepestKey: string | null = null;
    for (const key of keyArray) {
      if (hasValue(item[key as keyof FeatureRestrictionConfig])) {
        deepestKey = key;
      }
    }

    if (deepestKey) {
      if (!disabled[deepestKey]) disabled[deepestKey] = [];
      disabled[deepestKey].push(item);
    }
  });

  return disabled;
}

// Build disabled routes from config
// ...existing code...
export const getDisabledRoutes = (disableConfig: FeatureRestrictionConfig[]): Set<string> => {
  const disabledRoutes = new Set<string>();
  const hasValue = (val: unknown) => (typeof val === "string" ? val.trim() !== "" : val !== undefined && val !== null);

  disableConfig.forEach((config) => {
    if (!config?.isDisabled) return;

    // Only disable the feature route when no deeper scope is provided
    const hasDeeperScope =
      hasValue(config.tab) || hasValue(config.subTab) || hasValue(config.section) || hasValue(config.viewBy);
    if (hasDeeperScope) return;

    const route = featureRouteMap[config.feature];
    if (Array.isArray(route)) route.forEach((r) => disabledRoutes.add(r));
  });

  return disabledRoutes;
};

export const chunkArray = <T,>(arr: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(arr.length / size) }, (_, i) => arr.slice(i * size, i * size + size));

export const isAPIError = (error: unknown): error is IAPIError => {
  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    "data" in error &&
    typeof (error as Record<string, unknown>).status === "number" &&
    typeof (error as Record<string, unknown>).data === "object"
  );
};

export const isAccessDeniedError = (error: unknown): boolean => {
  if (isAPIError(error)) {
    return error.data?.errorCode === "ACCESS_DENIED";
  }

  return false;
};

export const getErrorMessage = (error: unknown, fallback: string): string => {
  if (!isAccessDeniedError(error)) {
    return fallback;
  }

  if (isAPIError(error)) {
    return error.data.message;
  }

  return fallback;
};
